import { jwtDecode, JwtPayload } from 'jwt-decode';
import { AuthTokens, authTokensSchema, UserRole } from 'types/auth';
import { safeGetItem, safeSetItem, safeRemoveItem } from 'utils/storage';

const SECURITY_SERVICE_URL = import.meta.env.VITE_SECURITY_SERVICE_URL!;
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const ROLE_CLAIM_SCHEMA = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

// Refresh slightly before the real expiry so we never race the boundary on a slow network.
const EXPIRY_SKEW_SECONDS = 30;

// ─── Storage ──────────────────────────────────────────────────────────────────

const readAccessToken = () => safeGetItem<string | null>(ACCESS_TOKEN_KEY, null);
const readRefreshToken = () => safeGetItem<string | null>(REFRESH_TOKEN_KEY, null);

const persistJwt = ({ accessToken, refreshToken }: AuthTokens) => {
  safeSetItem(ACCESS_TOKEN_KEY, accessToken);
  safeSetItem(REFRESH_TOKEN_KEY, refreshToken);
};

const clearJwt = () => {
  safeRemoveItem(ACCESS_TOKEN_KEY);
  safeRemoveItem(REFRESH_TOKEN_KEY);
};

// ─── Inspection ─────────────────────────────────────────────────────────────

// Secondes de validité restantes ; -Infinity si le jeton est illisible ou sans « exp ».
const remainingSeconds = (token: string): number => {
  try {
    const { exp } = jwtDecode<JwtPayload>(token);
    if (!exp) return Number.NEGATIVE_INFINITY;
    return exp - Date.now() / 1000;
  } catch {
    return Number.NEGATIVE_INFINITY;
  }
};

// ─── Refresh (single-flight) ──────────────────────────────────────────────────

let refreshPromise: Promise<string | undefined> | null = null;

const requestRefreshedTokens = async (expiredAccessToken: string, refreshToken: string): Promise<AuthTokens> => {
  const res = await fetch(`${SECURITY_SERVICE_URL}/Connect/refreshToken`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ expiredAccessToken, refreshToken }),
  });
  if (!res.ok) throw new Error(`Token refresh failed: ${res.status}`);
  return authTokensSchema.parse(await res.json());
};

// Coalesce concurrent callers onto a single in-flight refresh: with refresh-token
// rotation, parallel refreshes would reuse the same (now consumed) token and kill the session.
const refreshAccessToken = (expiredAccessToken: string, refreshToken: string): Promise<string | undefined> => {
  refreshPromise ??= (async () => {
    try {
      const tokens = await requestRefreshedTokens(expiredAccessToken, refreshToken);
      persistJwt(tokens);
      return tokens.accessToken;
    } catch {
      clearJwt();
      return undefined;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
};

// ─── Public API ───────────────────────────────────────────────────────────────

// Renouvelle le jeton d'accès à partir du jeton de rafraîchissement ; sans ce dernier, la session est close.
const refreshOrClear = (accessToken: string): Promise<string | undefined> => {
  const refreshToken = readRefreshToken();
  if (!refreshToken) {
    clearJwt();
    return Promise.resolve(undefined);
  }
  return refreshAccessToken(accessToken, refreshToken);
};

export type AccessTokenOptions = {
  /**
   * Validité minimale exigée, en secondes. En dessous, le jeton est renouvelé d'avance même s'il
   * est encore valide. Utile avant une requête longue (téléversement de pièces jointes) : le serveur
   * ne vérifie le jeton qu'à la réception complète du corps. Défaut : la marge de sécurité.
   */
  minRemainingSeconds?: number;
};

export const getAccessToken = async (options: AccessTokenOptions = {}): Promise<string | undefined> => {
  const accessToken = readAccessToken();
  if (!accessToken) return undefined;

  const minRemaining = Math.max(options.minRemainingSeconds ?? 0, EXPIRY_SKEW_SECONDS);
  if (remainingSeconds(accessToken) > minRemaining) return accessToken;

  return refreshOrClear(accessToken);
};

/**
 * Renouvelle le jeton même s'il paraît encore valide. Après un 401 sur une requête longue, le
 * serveur a pu le voir expirer pendant le transfert alors que le client le croyait bon.
 */
export const forceRefreshAccessToken = async (): Promise<string | undefined> => {
  const accessToken = readAccessToken();
  if (!accessToken) return undefined;
  return refreshOrClear(accessToken);
};

export const logoutUser = () => {
  clearJwt();
  refreshPromise = null;
  window.location.assign('/signIn');
};

export const getUserRoleFromToken = (token: string): UserRole | undefined => {
  try {
    return jwtDecode<Record<string, UserRole>>(token)[ROLE_CLAIM_SCHEMA];
  } catch {
    return undefined;
  }
};
